import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  Renderer2,
} from '@angular/core';

@Component({
  selector: 'xs-popup-menu',
  templateUrl: './popup-menu.component.html',
  styleUrls: ['./popup-menu.component.scss'],
})
export class PopupMenuComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Input() targetSelector: string;
  @Input() popupAlign: string;
  @Input() popupTop?: number;
  @Input() popupVerticalOffset: number;
  @Input() disableDropup: boolean = false;
  @Input() keepOpen: boolean = false;

  @ViewChild('popupWrapper', { static: false })
  popupElement: ElementRef;

  private closePopupClickAction = this.hidePopup.bind(this);

  public constructor(public elRef: ElementRef, public renderer: Renderer2) {}
  ngOnInit() {
    // Set default binding values if not specified
    this.popupVerticalOffset = this.popupVerticalOffset || 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isOpen && !changes.isOpen.isFirstChange()) {
      if (this.isOpen === false) {
        this.setIsClosed();
      } else {
        this.setIsOpen();
      }
      this.SetFocusOnOpenButton();
    }
  }

  private appendPopup = () => {
    if (this.targetSelector) {
      const targetElement = this.GetTargetElement();

      if (targetElement) {
        // Ensure parent has relative positioning so that our absolute positioning will work correctly
        const parent = this.elRef.nativeElement.parentElement;
        this.renderer.setStyle(parent, 'position', 'relative');

        // Define the Align requests
        const isAutoAlignRequired = this.popupAlign === undefined ? false : this.popupAlign.indexOf('auto') !== -1;
        const isRightAlignRequired = this.popupAlign === undefined ? false : this.popupAlign.indexOf('right') !== -1;

        // Cache values for computations

        this.popupElement.nativeElement.style.display = 'block';
        const popupWidth = this.popupElement.nativeElement.offsetWidth;
        this.popupElement.nativeElement.style.display = 'none';

        // Compute horizontal position within parent, left alignment is default
        let leftAlignOffset = targetElement.offsetLeft - parent.offsetLeft;

        if (isAutoAlignRequired) {
          if (isRightAlignRequired) {
            // align right if enough space
            if (targetElement.offsetLeft - parent.offsetLeft >= popupWidth) {
              leftAlignOffset = leftAlignOffset - popupWidth + targetElement.offsetWidth;
            } else if (parent.offsetLeft + parent.offsetWidth - targetElement.offsetLeft < popupWidth) {
              // not enough space for right or left align, so align center
              leftAlignOffset -= popupWidth / 2.0;
            }
          } else {
            // align left if enough space
            if (parent.offsetLeft + parent.clientWidth - targetElement.offsetLeft < popupWidth) {
              // not enough space to align left, so attempt to align right
              if (targetElement.offsetLeft - parent.offsetLeft >= popupWidth) {
                leftAlignOffset = leftAlignOffset - popupWidth + targetElement.offsetWidth;
              } else {
                // not enough space for right or left align, so align center
                leftAlignOffset -= popupWidth / 2.0;
              }
            }
          }
        } else if (isRightAlignRequired) {
          leftAlignOffset = leftAlignOffset - popupWidth + targetElement.offsetWidth;
        }
        // Compute vertical position within parent
        let top = targetElement.offsetTop - parent.offsetTop;

        if (this.popupTop !== undefined) {
          top = this.popupTop;
        } else {
          if (!this.disableDropup && this.shouldRiseUp(this.popupElement.nativeElement, targetElement)) {
            top -= this.popupElement.nativeElement.offsetHeight + this.popupVerticalOffset;
          } else {
            top += targetElement.offsetHeight + this.popupVerticalOffset;
          }
        }

        this.renderer.setStyle(this.popupElement.nativeElement, 'left', leftAlignOffset + 'px');
        this.renderer.setStyle(this.popupElement.nativeElement, 'top', top + 'px');
      }

      this.renderer.setStyle(this.popupElement.nativeElement, 'display', 'block');
    }
  };

  private closePopup = () => {
    this.renderer.setStyle(this.popupElement.nativeElement, 'display', 'none');
  };

  // Determines the direction of the popup, if the method returns false the menu drops down,
  // if the method returns true the menu rises up.

  private shouldRiseUp = (popupElement: any, targetElement: any): boolean => {
    const windowBottom = window.innerHeight + window.pageYOffset;
    return (
      targetElement.offsetTop + targetElement.offsetHeight + popupElement.offsetHeight + this.popupVerticalOffset >=
      windowBottom
    );
  };

  private setIsOpen = () => {
    this.appendPopup();
    // delay the click register so the current click event won't trigger it
    setTimeout(() => {
      document.addEventListener('click', this.closePopupClickAction);
      document.addEventListener('keydown', this.handleKeyDown);
    });
  };

  private setIsClosed = () => {
    if (!this.keepOpen) {
      this.closePopup();
      document.removeEventListener('click', this.closePopupClickAction);
      document.removeEventListener('keydown', this.handleKeyDown);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.hidePopup();
    }
  };

  private GetTargetElement() {
    return this.elRef.nativeElement.getRootNode().querySelector(this.targetSelector);
  }

  private SetFocusOnOpenButton() {
    const targetElement = this.GetTargetElement();
    // was opened/closed by chiclet button, keep focus here, else was closed by click elsewhere on document
    targetElement?.focus();
  }

  private hidePopup() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }
}
